import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { getProcessingActivities } from "@/lib/popia";
import { enquiryMailto } from "@/lib/enquiry";
import { ENQUIRY_EMAIL } from "@/lib/brand";

export const Route = createFileRoute("/legal/data-processing")({
  component: DataProcessingPage,
});

function DataProcessingPage() {
  const activities = getProcessingActivities();

  return (
    <MarketingLayout>
      <Helmet>
        <title>Data Processing Register | Larasoft</title>
        <meta
          name="description"
          content="Complete record of personal information processing activities at Larasoft in accordance with POPIA."
        />
        <link
          rel="canonical"
          href="https://larasoft.global/legal/data-processing"
        />
      </Helmet>
      <Section className="py-16">
        <div className="mx-auto max-w-6xl">
          <h1 className="mb-4 text-4xl font-bold">
            Data Processing Register (ROPA)
          </h1>
          <p className="mb-8 text-lg text-slate-700">
            Record of Processing Activities - Complete inventory of how Larasoft
            processes personal information
          </p>

          <div className="mb-8 rounded-lg bg-blue-50 p-6">
            <h2 className="mb-2 text-lg font-semibold text-blue-900">
              About This Register
            </h2>
            <p className="text-blue-800">
              This register documents all personal information processing
              activities at Larasoft, as required by the Protection of Personal
              Information Act (POPIA). It shows what data we collect, why we
              collect it, how we protect it, and how long we keep it.
            </p>
          </div>

          <div className="space-y-8">
            {activities.map((activity, i) => (
              <div
                key={activity.processingActivityId || i}
                className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <h2 className="mb-1 text-2xl font-semibold text-slate-900">
                      {activity.activityName}
                    </h2>
                    <p className="text-sm font-medium text-slate-500">
                      {activity.processingActivityId}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-sm font-medium ${
                      activity.riskLevel === "HIGH"
                        ? "bg-red-100 text-red-800"
                        : activity.riskLevel === "MEDIUM"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                    }`}
                  >
                    {activity.riskLevel} RISK
                  </span>
                </div>

                <p className="mb-6 text-slate-700">{activity.description}</p>

                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Data Categories
                    </h3>
                    <p className="text-slate-700">
                      {activity.dataCategoriesCollected}
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Data Subjects
                    </h3>
                    <p className="text-slate-700">{activity.dataSubjects}</p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Purpose of Processing
                    </h3>
                    <p className="text-slate-700">
                      {activity.purposeOfProcessing}
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Legal Basis
                    </h3>
                    <p className="text-slate-700">
                      {activity.legalBasisPOPIA}
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Storage Location
                    </h3>
                    <p className="text-slate-700">
                      {activity.storageLocation}
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Retention Period
                    </h3>
                    <p className="text-slate-700">
                      {activity.retentionPeriod}
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Security Measures
                    </h3>
                    <p className="text-slate-700">
                      {activity.securityMeasures}
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Transborder Transfers
                    </h3>
                    <p className="text-slate-700">
                      {activity.transborderTransfers}
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Data Recipients
                    </h3>
                    <p className="text-slate-700">
                      {activity.dataRecipientsProcessors}
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Owner
                    </h3>
                    <p className="text-slate-700">{activity.owner}</p>
                  </div>
                </div>

                {activity.specificDataFields && (
                  <details className="mt-4">
                    <summary className="cursor-pointer font-medium text-slate-700 hover:text-slate-900">
                      View Specific Data Fields
                    </summary>
                    <p className="mt-2 text-sm text-slate-600">
                      {activity.specificDataFields}
                    </p>
                  </details>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-lg bg-slate-50 p-6">
            <h3 className="mb-4 text-lg font-semibold">
              Questions About Data Processing?
            </h3>
            <p className="mb-4 text-slate-700">
              If you have questions about how we process your personal
              information, please contact our Information Officer:
            </p>
            <p className="text-slate-700">
              <a
                href={enquiryMailto("/legal/data-processing")}
                className="text-blue-600 underline"
              >
                {ENQUIRY_EMAIL}
              </a>{" "}
              | +27 82 457 8390
            </p>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
